/**
 * ServerDestroyModal — Destruction d'une VM Hetzner (irréversible).
 *
 * Double-confirmation :
 *   - checkbox "J'ai compris"
 *   - retape du nom exact du serveur
 *
 * @spec specs/app/admin/dedicated-servers.md §6.7
 */

import { Alert, Checkbox, Input, Modal } from 'antd';
import { Trash2 } from 'lucide-react';
import { useState, type FC } from 'react';
import type { DedicatedServer } from '@/types/dedicatedServer';
import { useDedicatedServerActions } from '../hooks/useDedicatedServerActions';

interface ServerDestroyModalProps {
  server: DedicatedServer;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const MIN_REASON = 10;
const MAX_REASON = 500;

const ServerDestroyModal: FC<ServerDestroyModalProps> = ({
  server,
  open,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [nameTyped, setNameTyped] = useState('');
  const { destroy, isDestroying } = useDedicatedServerActions(server.id);

  const nameMatches = nameTyped.trim() === server.name;
  const canSubmit = confirmed && nameMatches && reason.length >= MIN_REASON;

  const handleOk = () => {
    if (!canSubmit) return;
    destroy(reason, {
      onSuccess: () => {
        setReason('');
        setConfirmed(false);
        setNameTyped('');
        onClose();
        onSuccess?.();
      },
    });
  };

  const handleCancel = () => {
    setReason('');
    setConfirmed(false);
    setNameTyped('');
    onClose();
  };

  return (
    <Modal
      title={
        <span className="inline-flex items-center gap-2">
          <Trash2 className="h-4 w-4 text-red-600" />
          Détruire le serveur
        </span>
      }
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Détruire définitivement"
      cancelText="Annuler"
      okButtonProps={{
        danger: true,
        type: 'primary',
        loading: isDestroying,
        disabled: !canSubmit,
      }}
      destroyOnClose
      width={560}
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          La VM Hetzner <strong>{server.name}</strong> sera supprimée définitivement.
        </p>
        <Alert
          type="error"
          showIcon
          message="IRRÉVERSIBLE"
          description="Tous les workspaces, images Docker et données locales seront définitivement perdus. Les snapshots existants peuvent subsister selon la politique Hetzner."
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Raison (obligatoire, {MIN_REASON}–{MAX_REASON} caractères)
          </label>
          <Input.TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex: ticket #9999 — orga résiliée, cleanup post-churn"
            maxLength={MAX_REASON}
            showCount
            rows={3}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Saisir le nom du serveur pour confirmer :{' '}
            <code className="font-mono text-red-600">{server.name}</code>
          </label>
          <Input
            value={nameTyped}
            onChange={(e) => setNameTyped(e.target.value)}
            placeholder={server.name}
            status={nameTyped.length > 0 && !nameMatches ? 'error' : undefined}
          />
        </div>
        <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}>
          Je confirme avoir compris que cette action est irréversible
        </Checkbox>
      </div>
    </Modal>
  );
};

export default ServerDestroyModal;
